<template>
  <DefaultHeader />
  <main v-if="!$store.state.hiddenBody" class="driver-details-page">
    <div class="page-container">
      <nav class="breadcrumb" aria-label="Fil d'Ariane">
        <router-link to="/drivers">Chauffeurs</router-link>
        <span aria-hidden="true">/</span>
        <span aria-current="page">{{ driver ? fullName : 'Fiche chauffeur' }}</span>
      </nav>

      <div v-if="loading" class="state-card" role="status">
        <span class="spinner" aria-hidden="true"></span>
        <span>Chargement de la fiche chauffeur…</span>
      </div>

      <div v-else-if="pageError" class="state-card state-error" role="alert">
        <div class="state-symbol" aria-hidden="true">!</div>
        <h1>{{ pageErrorTitle }}</h1>
        <p>{{ pageError }}</p>
        <div class="state-actions">
          <button v-if="canRetry" type="button" class="primary-button" @click="loadDriver">Réessayer</button>
          <router-link to="/drivers" class="secondary-button">Retour aux chauffeurs</router-link>
        </div>
      </div>

      <template v-else-if="driver">
        <div v-if="feedback" class="notice success" role="status">{{ feedback }}</div>

        <header class="profile-hero">
          <div class="hero-main">
            <div class="profile-avatar" aria-label="Avatar du chauffeur">{{ initials }}</div>
            <div class="hero-text">
              <div class="eyebrow">FICHE CHAUFFEUR <span class="id-label">N° {{ driver.id }}</span></div>
              <h1>{{ fullName }}</h1>
              <div class="hero-meta">
                <span class="status-pill" :class="driver.status === 'ACTIVE' ? 'status-active' : 'status-inactive'">
                  <span class="status-dot" aria-hidden="true"></span>{{ statusLabel }}
                </span>
                <span class="meta-separator" aria-hidden="true"></span>
                <span>{{ badgeCount }} badge{{ badgeCount !== 1 ? 's' : '' }} associé{{ badgeCount !== 1 ? 's' : '' }}</span>
              </div>
            </div>
          </div>
          <div class="hero-actions">
            <button v-if="canManage && !isEditing" type="button" class="primary-button" @click="beginEdit">
              <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 20h9M16 4a2.8 2.8 0 0 1 4 4L9 19l-5 1 1-5L16 4Z"/></svg>
              Modifier la fiche
            </button>
            <button v-if="isEditing" type="button" class="secondary-button" :disabled="busy" @click="cancelEdit">Revenir à la fiche</button>
          </div>
        </header>

        <div class="content-layout" :class="{ 'with-form': isEditing }">
          <div class="main-column">
            <section class="section-card" aria-labelledby="info-title">
              <div class="section-header">
                <div>
                  <div class="section-eyebrow">IDENTITÉ</div>
                  <h2 id="info-title">Informations générales</h2>
                  <p>Les informations enregistrées pour ce chauffeur.</p>
                </div>
              </div>
              <div class="details-grid">
                <div class="detail-item"><span>Prénom</span><strong>{{ driver.firstName || '—' }}</strong></div>
                <div class="detail-item"><span>Nom</span><strong>{{ driver.lastName || '—' }}</strong></div>
                <div class="detail-item"><span>Identifiant chauffeur</span><strong class="numeric">#{{ driver.id }}</strong></div>
                <div class="detail-item"><span>Statut</span><strong>{{ statusLabel }}</strong></div>
                <div class="detail-item detail-full">
                  <span>Dernière activité</span>
                  <strong>{{ formattedLastActivity }}</strong>
                  <small v-if="!driver.lastActivity">Aucune activité enregistrée pour le moment.</small>
                </div>
              </div>
            </section>

            <section class="section-card" aria-labelledby="badges-title">
              <div class="section-header badges-heading">
                <div>
                  <div class="section-eyebrow">IDENTIFICATION</div>
                  <h2 id="badges-title">Badges associés</h2>
                  <p>Badges actuellement rattachés à ce chauffeur.</p>
                </div>
                <span class="count-bubble">{{ badgeCount }}</span>
              </div>
              <div v-if="!badgeCount" class="empty-section">
                <div class="empty-icon" aria-hidden="true">▣</div>
                <div><strong>Aucun badge associé</strong><p>Les badges associés au chauffeur apparaîtront ici.</p></div>
              </div>
              <div v-else class="badge-list">
                <div v-for="badge in driver.badges" :key="badge.id" class="driver-badge-row">
                  <div class="badge-icon" aria-hidden="true">▣</div>
                  <div class="badge-main">
                    <strong>{{ badge.ref || 'Sans référence' }}</strong>
                    <span>Slot balise : {{ badge.slot != null ? badge.slot : '—' }}</span>
                  </div>
                  <span class="small-status" :class="badge.status === 'ACTIVE' ? 'small-active' : (badge.status === 'NEW' ? 'small-new' : 'small-disabled')">{{ badgeStatus(badge.status) }}</span>
                </div>
              </div>
              <p v-if="canManage && !isEditing" class="inline-hint">Les associations de badges sont modifiables depuis « Modifier la fiche ».</p>
            </section>
          </div>

          <aside v-if="isEditing" class="edit-column" aria-label="Formulaire de modification du chauffeur">
            <DriverForm
              :key="`driver-${driver.id}-${formRevision}`"
              :driver="editableDriver"
              :saving="saving"
              :deleting="deleting"
              :error="formError"
              @save="saveDriver"
              @delete="deleteDriver"
              @badges-changed="refreshAfterBadgesChange"
              @cancel="cancelEdit"
            />
          </aside>
        </div>

        <section class="future-section" aria-labelledby="future-title">
          <div class="future-header">
            <div class="section-eyebrow">PROCHAINEMENT</div>
            <h2 id="future-title">Evolutions de la page chauffeur</h2>
            <p>Ces fonctionnalités ne sont pas encore disponibles.</p>
          </div>
          <div class="future-grid">
            <article class="future-card">
              <div class="future-icon" aria-hidden="true"><svg viewBox="0 0 24 24"><path d="m4 19 5-5 4 3 7-10"/><path d="M15 7h5v5"/></svg></div>
              <div><h3>Trajets du chauffeur</h3><p>Historique et consultation des déplacements.</p></div>
              <span class="upcoming">À venir</span>
            </article>
            <article class="future-card">
              <div class="future-icon" aria-hidden="true"><svg viewBox="0 0 24 24"><path d="M4 19V5M4 19h16M8 15v-4M13 15V7M18 15v-8"/></svg></div>
              <div><h3>Analyse de conduite</h3><p>Indicateurs, graphiques et évolution dans le temps.</p></div>
              <span class="upcoming">À venir</span>
            </article>
          </div>
        </section>
      </template>
    </div>
  </main>
  <DefaultFooter />
</template>

<script>
import DefaultHeader from '@/components/DefaultHeader.vue';
import DefaultFooter from '@/components/DefaultFooter.vue';
import DriverRepository from '@/repositories/driver.repo.js';
import DriverForm from './sections/DriverForm.vue';

export default {
  name: 'DriverDetails',
  components: { DefaultHeader, DefaultFooter, DriverForm },
  data() {
    return {
      driver: null,
      loading: true,
      pageError: '',
      pageErrorTitle: '',
      canRetry: false,
      isEditing: false,
      saving: false,
      deleting: false,
      formError: '',
      feedback: '',
      formRevision: 0,
      requestSequence: 0
    };
  },
  computed: {
    canManage() { return Boolean(this.$isClientAdmin || this.$isAdmin); },
    busy() { return this.saving || this.deleting; },
    driverId() { return this.$route.params.driverId; },
    fullName() {
      return [this.driver?.firstName, this.driver?.lastName].filter(Boolean).join(' ') || 'Chauffeur sans nom';
    },
    initials() {
      return [this.driver?.firstName, this.driver?.lastName]
        .filter(Boolean).map(value => String(value).charAt(0)).join('').slice(0, 2).toLocaleUpperCase('fr') || '—';
    },
    statusLabel() { return this.driver?.status === 'ACTIVE' ? 'Actif' : this.driver?.status === 'INACTIVE' ? 'Inactif' : 'Statut non renseigné'; },
    badgeCount() { return this.driver?.badges?.length || 0; },
    editableDriver() { return this.driver ? { ...this.driver, displayLastActivity: this.formattedLastActivity } : null; },
    formattedLastActivity() {
      if (!this.driver?.lastActivity) return 'Aucune activité enregistrée';
      const date = new Date(this.driver.lastActivity);
      return Number.isNaN(date.getTime()) ? 'Date indisponible' : date.toLocaleString('fr-FR', {
        day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit'
      });
    }
  },
  watch: {
    driverId() {
      this.isEditing = false;
      this.driver = null;
      this.formError = '';
      this.feedback = '';
      this.loadDriver();
    }
  },
  mounted() { this.loadDriver(); },
  beforeUnmount() { this.requestSequence++; },
  methods: {
    badgeStatus(status) {
      return { ACTIVE: 'Actif', INACTIVE: 'Inactif', NEW: 'À valider', DISABLED: 'Désactivé' }[status] || 'Inconnu';
    },
    async loadDriver({ silent = false } = {}) {
      const id = Number(this.driverId);
      const sequence = ++this.requestSequence;
      if (!Number.isSafeInteger(id) || id <= 0) {
        this.driver = null;
        this.loading = false;
        this.pageErrorTitle = 'Identifiant incorrect';
        this.pageError = 'La fiche demandée ne possède pas un identifiant valide.';
        this.canRetry = false;
        return false;
      }
      if (!silent) this.loading = true;
      if (!silent) this.pageError = '';
      try {
        const driver = await DriverRepository.getDriver(id);
        if (sequence !== this.requestSequence) return false;
        this.driver = { ...driver, badges: Array.isArray(driver?.badges) ? driver.badges : [] };
        return true;
      } catch (err) {
        if (sequence !== this.requestSequence) return false;
        console.error('Chargement fiche chauffeur :', err);
        const status = err.response?.status;
        if (!silent) {
          this.pageErrorTitle = status === 404 ? 'Chauffeur introuvable' : status === 403 ? 'Accès refusé' : 'Impossible de charger cette fiche';
          this.pageError = status === 404 ? "Ce chauffeur n'existe pas ou n'est plus disponible." : status === 403 ? "Vous n'avez pas accès à ce chauffeur." : 'Le serveur ne répond pas. Vous pouvez réessayer.';
          this.canRetry = status !== 403 && status !== 404;
          this.driver = null;
        }
        return false;
      } finally {
        if (sequence === this.requestSequence && !silent) this.loading = false;
      }
    },
    beginEdit() {
      if (!this.canManage || !this.driver) return;
      this.feedback = '';
      this.formError = '';
      this.formRevision++;
      this.isEditing = true;
    },
    cancelEdit() {
      if (this.busy) return;
      this.isEditing = false;
      this.formError = '';
    },
    async refreshAfterBadgesChange() {
      // DriverBadges gère ses mutations; on ne remonte que la fiche.
      const ok = await this.loadDriver({ silent: true });
      if (!ok) this.formError = 'Association mise à jour, mais la fiche n’a pas pu être actualisée.';
    },
    async saveDriver(values) {
      if (!this.canManage || this.busy || !this.driver) return;
      this.saving = true;
      this.formError = '';
      this.feedback = '';
      try {
        await DriverRepository.updateDriver(this.driver.id, values);
        if (!await this.loadDriver({ silent: true })) {
          this.formError = 'Modification enregistrée, mais impossible de rafraîchir les informations.';
          return;
        }
        this.isEditing = false;
        this.feedback = 'Les informations du chauffeur ont été mises à jour.';
      } catch (err) {
        console.error('Modification chauffeur :', err);
        this.formError = "Impossible d'enregistrer les modifications.";
      } finally {
        this.saving = false;
      }
    },
    async deleteDriver() {
      if (!this.canManage || this.busy || !this.driver) return;
      this.deleting = true;
      this.formError = '';
      try {
        await DriverRepository.deleteDriver(this.driver.id);
        await this.$router.replace('/drivers');
      } catch (err) {
        console.error('Suppression chauffeur :', err);
        this.formError = 'Impossible de supprimer ce chauffeur.';
      } finally {
        this.deleting = false;
      }
    }
  }
};
</script>

<style scoped>
.driver-details-page { min-height: 70vh; background: #f5f7fb; color: #17243b; padding: 32px 24px 80px; }
.page-container { max-width: 1420px; margin: 0 auto; }
.breadcrumb { display: flex; align-items: center; gap: 12px; font-size: 13px; color: #8a97aa; margin: 0 0 24px; }
.breadcrumb a { color: #315c94; font-weight: 700; text-decoration: none; }
.breadcrumb a:hover { text-decoration: underline; }
.breadcrumb span[aria-current] { color: #5f6f86; }
.profile-hero { background: #fff; border: 1px solid #e3e9f1; border-radius: 20px; padding: 32px; box-shadow: 0 12px 32px rgba(20,43,74,.045); display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 22px; margin-bottom: 22px; position: relative; overflow: hidden; }
.profile-hero::before { content: ''; position: absolute; top: 0; left: 0; right: 0; height: 4px; background: linear-gradient(90deg, #1b4f95, #5a9fe2); }
.hero-main { display: flex; align-items: center; gap: 24px; min-width: 0; }
.profile-avatar { display: grid; place-items: center; flex: none; width: 92px; height: 92px; border-radius: 24px; background: linear-gradient(145deg,#e1edfc,#f0f6ff); border: 1px solid #d9e8f9; color: #285a96; font-size: 30px; font-weight: 800; letter-spacing: -.045em; }
.hero-text { min-width: 0; }
.eyebrow,.section-eyebrow { color: #657f9f; font-size: 10px; font-weight: 800; letter-spacing: .135em; }
.id-label { letter-spacing: 0; color: #8b9bb1; font-weight: 700; margin-left: 7px; }
h1 { font-size: clamp(28px,3.4vw,40px); letter-spacing: -.04em; font-weight: 780; margin: 8px 0 15px; line-height: 1.15; overflow-wrap: anywhere; }
.hero-meta { display: flex; flex-wrap: wrap; align-items: center; gap: 11px; color: #6b7890; font-size: 13px; }
.meta-separator { width: 4px; height: 4px; border-radius: 50%; background: #bbc6d4; }
.status-pill { display: inline-flex; align-items: center; gap: 8px; font-size: 12px; padding: 7px 12px; border-radius: 999px; font-weight: 750; }
.status-active { background: #eaf7f0; color: #17794b; }
.status-inactive { background: #edf1f5; color: #65758b; }
.status-dot { width: 7px; height: 7px; background: currentColor; border-radius: 50%; }
.hero-actions { display: flex; gap: 10px; flex-wrap: wrap; }
.primary-button,.secondary-button { display: inline-flex; justify-content: center; align-items: center; gap: 9px; border-radius: 10px; padding: 12px 17px; text-decoration: none; font-size: 13px; font-weight: 750; cursor: pointer; }
.primary-button { background: #1b4f95; border: 1px solid #1b4f95; color: #fff; }
.primary-button:hover { background: #17437e; }
.secondary-button { border: 1px solid #d8e2ee; background: #fff; color: #425d7f; }
.secondary-button:hover { background: #f4f8fc; }
.primary-button svg { width: 17px; height: 17px; fill: none; stroke: currentColor; stroke-width: 1.8; stroke-linecap: round; stroke-linejoin: round; }
.content-layout { display: grid; grid-template-columns: minmax(0, 1fr); align-items: start; gap: 22px; }
.content-layout.with-form { grid-template-columns: minmax(0,1fr) minmax(340px, 430px); }
.main-column { display: grid; gap: 22px; min-width: 0; }
.section-card,.edit-column { background: #fff; border: 1px solid #e3e9f1; border-radius: 16px; box-shadow: 0 10px 28px rgba(20,43,74,.045); min-width: 0; overflow: hidden; }
.section-header { padding: 26px 28px 20px; border-bottom: 1px solid #edf0f5; }
.section-header h2,.future-header h2 { font-size: 19px; font-weight: 750; letter-spacing: -.025em; margin: 6px 0; }
.section-header p,.future-header p { color: #78869a; font-size: 13px; margin: 0; line-height: 1.55; }
.details-grid { padding: 3px 28px 12px; display: grid; grid-template-columns: repeat(2, minmax(0,1fr)); column-gap: 30px; }
.detail-item { display: flex; flex-direction: column; gap: 8px; min-width: 0; border-bottom: 1px solid #eef2f7; padding: 22px 0; }
.detail-item:nth-last-child(-n+1) { border-bottom: none; }
.detail-item span { color: #8794a8; font-size: 12px; font-weight: 650; }
.detail-item strong { font-size: 15px; color: #203049; font-weight: 700; overflow-wrap: anywhere; }
.detail-item small { color: #99a5b5; font-size: 12px; }
.detail-full { grid-column: 1 / -1; }
.numeric { font-variant-numeric: tabular-nums; }
.badges-heading { display: flex; justify-content: space-between; align-items: center; gap: 12px; }
.count-bubble { border-radius: 10px; font-size: 13px; font-weight: 800; color: #2b558b; background: #eaf1fb; padding: 8px 12px; }
.badge-list { padding: 8px 28px; }
.driver-badge-row { display: flex; align-items: center; gap: 14px; padding: 14px 0; border-bottom: 1px solid #eef2f7; }
.driver-badge-row:last-child { border-bottom: none; }
.badge-icon { display: grid; place-items: center; flex: none; width: 40px; height: 40px; border: 1px solid #e1eaf6; background: #f2f7fd; color: #406999; border-radius: 12px; font-size: 19px; }
.badge-main { flex: 1; min-width: 0; display: grid; gap: 5px; }
.badge-main strong { font-size: 13px; overflow-wrap: anywhere; }
.badge-main span { color: #8290a6; font-size: 12px; overflow-wrap: anywhere; }
.small-status { flex: none; font-size: 11px; border-radius: 999px; padding: 6px 10px; font-weight: 750; }
.small-active { color: #17794b; background: #eaf7f0; }
.small-new { color: #93600e; background: #fff3d7; }
.small-disabled { color: #677488; background: #eef1f5; }
.inline-hint { border-top: 1px solid #eef2f7; padding: 15px 28px; font-size: 12px; color: #7b899c; margin: 0; background: #fcfdff; }
.empty-section { display: flex; align-items: center; gap: 16px; padding: 32px 28px; }
.empty-icon { display: grid; place-items: center; flex: none; width: 46px; height: 46px; border-radius: 13px; color: #6683a6; background: #f0f5fb; font-size: 22px; }
.empty-section strong { font-size: 13px; }
.empty-section p { color: #8391a5; font-size: 12px; margin: 6px 0 0; }
.edit-column { align-self: start; }
.future-section { margin-top: 35px; }
.future-header { margin-bottom: 18px; }
.future-header h2 { margin: 6px 0 5px; }
.future-grid { display: grid; grid-template-columns: repeat(2,minmax(0,1fr)); gap: 16px; }
.future-card { display: flex; align-items: center; gap: 16px; padding: 22px; border-radius: 14px; border: 1px dashed #d9e2ed; background: #f9fbfe; position: relative; }
.future-icon { display: grid; place-items: center; flex: none; width: 42px; height: 42px; border-radius: 11px; background: #edf3fb; color: #728eae; }
.future-icon svg { width: 22px; height: 22px; fill: none; stroke: currentColor; stroke-width: 1.7; stroke-linecap: round; stroke-linejoin: round; }
.future-card h3 { font-size: 14px; color: #526780; margin: 0 0 4px; }
.future-card p { font-size: 12px; color: #8d9ab0; margin: 0; line-height: 1.5; }
.upcoming { align-self: start; white-space: nowrap; font-size: 10px; font-weight: 750; color: #8a9cb3; background: #edf2f8; border-radius: 99px; padding: 6px 9px; margin-left: auto; }
.state-card { min-height: 300px; background: #fff; border: 1px solid #e3e9f1; border-radius: 18px; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 14px; text-align: center; padding: 35px; color: #62738a; }
.state-card h1 { font-size: 24px; margin: 0; }
.state-card p { font-size: 14px; margin: 0; max-width: 480px; }
.state-symbol { width: 52px; height: 52px; border-radius: 50%; display: grid; place-items: center; background: #fbeded; color: #ac4e4e; font-weight: 800; }
.state-actions { display: flex; flex-wrap: wrap; gap: 10px; margin-top: 12px; }
.spinner { width: 28px; height: 28px; border: 3px solid #dae7f4; border-top-color: #1b4f95; border-radius: 50%; animation: spin .75s linear infinite; }
@keyframes spin { to { transform: rotate(360deg); } }
.notice { padding: 14px 18px; border-radius: 10px; margin-bottom: 16px; font-size: 13px; }
.notice.success { color: #136243; background: #eaf7ef; border: 1px solid #c4e8d2; }
@media (max-width: 1040px) { .content-layout.with-form { grid-template-columns: 1fr; } .content-layout.with-form .edit-column { order: -1; } }
@media (max-width: 740px) { .driver-details-page { padding: 22px 14px 54px; } .profile-hero { padding: 22px; align-items: flex-start; } .hero-main { gap: 15px; align-items: flex-start; } .profile-avatar { width: 68px; height: 68px; border-radius: 17px; font-size: 24px; } .section-header { padding: 22px 20px 18px; } .details-grid { padding: 2px 20px 12px; } .badge-list { padding: 8px 20px; } .inline-hint { padding: 15px 20px; } .future-grid { grid-template-columns: 1fr; } }
@media (max-width: 480px) { .details-grid { grid-template-columns: 1fr; } .detail-full { grid-column: auto; } .hero-main { flex-wrap: wrap; } .future-card { flex-wrap: wrap; } .upcoming { margin-left: 0; } }
</style>
